export interface JwtPayload {
  accountCode?: string;
  account_code?: string;
  [key: string]: any;
}

/**
 * Decodes a JWT token without verification
 * @param token - The JWT token string (with or without "Bearer " prefix)
 * @returns The decoded payload or null if invalid
 */
export const decodeJwt = (token: string): JwtPayload | null => {
  try {
    // Remove "Bearer " prefix if present
    const cleanToken = token.replace(/^Bearer\s+/i, '');

    // JWT tokens have 3 parts separated by dots
    const parts = cleanToken.split('.');
    if (parts.length !== 3) {
      console.error('Invalid JWT token format');
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    const decodedPayload = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));

    return JSON.parse(decodedPayload);
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return null;
  }
};

/**
 * Extracts the account code from a JWT token
 * @param token - The JWT token string
 * @returns The account code or null if not found
 */
export const getAccountCodeFromToken = (token: string): string | null => {
  const payload = decodeJwt(token);
  const sessionInfo = JSON.parse(payload?.sub || null);

  return sessionInfo?.targetAccountCode || sessionInfo?.accountCode || null;
};

/**
 * Verifies that the account code in the JWT token matches the expected account code
 * @param token - The JWT token string
 * @param expectedAccountCode - The expected account code to match against
 * @returns true if account codes match, false if there's a mismatch
 */
export const verifyAccountCode = (token: string, expectedAccountCode: string | null): boolean => {
  if (!token || !expectedAccountCode) {
    return false;
  }

  const tokenAccountCode = getAccountCodeFromToken(token);

  return !!tokenAccountCode && tokenAccountCode === expectedAccountCode;
};
